import _ from 'lodash'

import api from '@/api'
import types from './types'
import handleError from '@/lib/handleError.js'
import { serializeProblemSolving } from './serializer'

function getFileNameFromDisposition(disposition) {
  if (!disposition) return undefined

  const match = disposition.match(/filename="?([^";]+)"?/i)

  if (!match || !match[1]) {
    return undefined
  }

  return decodeURIComponent(match[1])
}

function getFieldsToTranslate(rev) {
  // This method is apparently duplicated in another action, but if you look carefully the fields to be translated are not the same, we do this because each word that is translated costs money and we do not want to waste resources
  const fieldsToTranslate = [
    'name',
    'problemAnalysis.businessRequirements',
    'problemAnalysis.containment',
    'problemAnalysis.goals',
    'problemAnalysis.measureDescription',
    'problemAnalysis.problem',
    'problemBreakdown.how',
    'problemBreakdown.howMuch',
    'problemBreakdown.what',
    'problemBreakdown.when',
    'problemBreakdown.where',
    'problemBreakdown.who',
    'problemBreakdown.why',
    'reviewPhase.estimationExplanation',
    'solutionDescription',
  ]

  rev?.rootCauses?.forEach?.((rootCause, i) => {
    fieldsToTranslate.push(
      `rootCauses[${i}].correctiveAction`,
      `rootCauses[${i}].correctiveLearning`,
      `rootCauses[${i}].description`,
      `rootCauses[${i}].name`,
      `rootCauses[${i}].preventiveAction`
    )
    rootCause?.analysisWhyBreakdown?.forEach?.((why, j) =>
      fieldsToTranslate.push(`rootCauses[${i}].analysisWhyBreakdown[${j}]`)
    )
  })

  rev?.processGaps?.forEach?.((gap, i) => {
    fieldsToTranslate.push(
      `processGaps[${i}].conclusion`,
      `processGaps[${i}].expectation`,
      `processGaps[${i}].reality`
    )
  })

  return fieldsToTranslate
}

export default {
  create(context, { organizationId, data }) {
    return api.problemsolving
      .create(organizationId, data)
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, rev)
        return {
          revId: rev.id,
        }
      })
  },

  async update(context, { organizationId, revId, data }) {
    const problemSolving = await api.problemsolving
      .update(organizationId, revId, serializeProblemSolving(data))
      .then(response => response.data)
    context.commit(types.SET_REV, problemSolving)
    return { revId }
  },

  async get(context, { organizationId, revId }) {
    const rev = await api.problemsolving
      .getById(organizationId, revId)
      .then(response => response.data)

    context.commit(types.SET_REV, rev)
    return rev
  },

  delete(context, { organizationId, revId }) {
    return api.problemsolving.delete(organizationId, revId)
  },

  like(context, payload) {
    return api.like
      .add(payload.organizationId, 'problem-solving', payload.revId)
      .then(() =>
        api.problemsolving.getById(payload.organizationId, payload.revId)
      )
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, rev)
        context.dispatch('refreshAccountData', {}, { root: true })
        return rev
      })
      .catch(handleError)
  },

  unlike(context, payload) {
    return api.like
      .remove(payload.organizationId, 'problem-solving', payload.revId)
      .then(() =>
        api.problemsolving.getById(payload.organizationId, payload.revId)
      )
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, rev)
        return rev
      })
      .catch()
  },

  approveRev: function (context, payload) {
    return api.approvals
      .add(payload.organizationId, 'problem-solving', payload.revId)
      .then(() => {
        return api.problemsolving.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, rev)
        context.dispatch('refreshAccountData', {}, { root: true })
        return rev
      })
      .catch(handleError)
  },

  disapproveRev: function (context, payload) {
    return api.approvals
      .remove(
        payload.organizationId,
        'problem-solving',
        payload.revId,
        payload.approvalId
      )
      .then(() => {
        return api.problemsolving.getById(payload.organizationId, payload.revId)
      })
      .then(({ data: rev }) => {
        context.commit(types.SET_REV, rev)
        return rev
      })
      .catch()
  },

  async translateRev(context, { organizationId, revId, targetLanguage }) {
    const { data: rev } = await api.problemsolving.getById(
      organizationId,
      revId
    )
    const fieldsToTranslate = getFieldsToTranslate(rev)

    const translatedRev = Object.assign({}, rev)

    for (const field of fieldsToTranslate) {
      if (!_.get(rev, field)) continue
      const translatedField = await api.translate.text(
        organizationId,
        _.get(rev, field),
        targetLanguage
      )
      _.set(translatedRev, field, translatedField.data.translatedText)
    }

    context.commit(types.SET_REV, translatedRev)

    return translatedRev
  },

  async validationRequest(context, payload) {
    const response = await api.validationRequest.add(
      payload.organizationId,
      'problem-solving',
      payload.revId,
      payload.assignees
    )
    return response
  },

  async updateRequestStatus(context, payload) {
    return await api.validationRequest.update(
      payload.organizationId,
      'problem-solving',
      payload.revId,
      payload.requestId,
      payload.status
    )
  },

  async generatePowerPoint(
    context,
    { organizationId, revId, layout = 'default' }
  ) {
    let url
    let link

    try {
      const revType = 'problemSolving'

      const response = await api.powerpoint.generatePowerPoint(
        organizationId,
        revId,
        revType,
        layout
      )
      const fileName =
        getFileNameFromDisposition(response.headers?.['content-disposition']) ||
        'presentation.pptx'

      const blob = new window.Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })

      if (window.navigator && window.navigator.msSaveBlob) {
        window.navigator.msSaveBlob(blob, fileName)
        return
      }

      url = window.URL.createObjectURL(blob)
      link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      handleError(error)
    } finally {
      if (url) {
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
      }
    }
  },
}
